/**
 * Pure helpers for the task-assignee search-and-select control.
 *
 * Kept free of React / localStorage so the same matching rules can be
 * exercised from a Node self-check.  The UI layer (``UserSearchSelect``)
 * owns fetching and persistence.
 */

export const RECENT_ASSIGNEE_LIMIT = 3;
export const SEARCH_RESULT_LIMIT = 8;

/** Same contract as ``complaints_management.external_users.EXTERNAL_ROLE_NAME``. */
export const EXTERNAL_ASSIGNEE_ROLE = 'auretics-users';

/**
 * ``True`` for Auretics-portal / public-API complaint accounts.
 *
 * Mirrors ``is_external_user`` so the picker can drop them even when:
 *   * ``GET /api/super_admin/users/?external=false`` is ignored, or
 *   * a Recent chip was cached before that filter existed.
 *
 * Elevated accounts are never treated as external (same fail-closed
 * rule as the server).  ``role`` may be a PK, a ``{name}`` object, or
 * absent — we also read ``role_name`` / ``is_external`` from UserSerializer.
 */
export function isExternalAssignee(user) {
  if (!user || typeof user !== 'object') return false;
  if (user.is_superuser || user.is_admin) return false;
  if (user.is_external || user.is_external_user) return true;
  const fromRoleObj = user.role && typeof user.role === 'object' ? user.role.name : '';
  const roleName = String(fromRoleObj || user.role_name || '')
    .trim()
    .toLowerCase();
  return roleName === EXTERNAL_ASSIGNEE_ROLE;
}

/**
 * Normalise the various user shapes the SPA sees (``UserSerializer``,
 * ``/me/``, ``TaskSerializer.assignees``) into one picker row.
 */
export function normalizeUser(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = Number(raw.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const fromRoleObj = raw.role && typeof raw.role === 'object' ? raw.role.name : '';
  return {
    id,
    first_name: raw.first_name ?? null,
    last_name: raw.last_name ?? null,
    full_name: raw.full_name ?? null,
    username: raw.username ?? null,
    email: raw.email ?? null,
    phone_number: raw.phone_number ?? raw.mobile ?? raw.phone ?? null,
    role_name: raw.role_name || fromRoleObj || null,
    is_external: !!(raw.is_external || raw.is_external_user),
    is_admin: !!raw.is_admin,
    is_superuser: !!raw.is_superuser,
  };
}

export function unwrapUserList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return Array.isArray(data.results) ? data.results : [];
}

export function displayName(user) {
  if (!user) return '';
  const fromParts = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return (
    (user.full_name && String(user.full_name).trim()) ||
    fromParts ||
    user.username ||
    user.email ||
    `User ${user.id}`
  );
}

export function displayMeta(user) {
  if (!user) return '';
  return [user.email, user.phone_number].filter(Boolean).join(' · ');
}

function haystack(user) {
  return [
    user.first_name,
    user.last_name,
    user.full_name,
    user.username,
    user.email,
    user.phone_number,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Match a typed query against name, email or mobile (digits ignored). */
export function userMatchesSearch(user, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const row = normalizeUser(user);
  if (!row) return false;
  if (haystack(row).includes(q)) return true;
  const digits = q.replace(/\D/g, '');
  if (digits.length >= 3) {
    const phoneDigits = String(row.phone_number || '').replace(/\D/g, '');
    if (phoneDigits.includes(digits)) return true;
  }
  return false;
}

export function mergeUser(existing, incoming) {
  const a = normalizeUser(existing);
  const b = normalizeUser(incoming);
  if (!a) return b;
  if (!b) return a;
  return {
    id: a.id,
    first_name: b.first_name || a.first_name,
    last_name: b.last_name || a.last_name,
    full_name: b.full_name || a.full_name,
    username: b.username || a.username,
    email: b.email || a.email,
    phone_number: b.phone_number || a.phone_number,
    role_name: b.role_name || a.role_name,
    is_external: !!(b.is_external || a.is_external),
    is_admin: !!(b.is_admin || a.is_admin),
    is_superuser: !!(b.is_superuser || a.is_superuser),
  };
}

export function uniqueUsers(rows) {
  const out = [];
  const seen = new Set();
  for (const raw of rows || []) {
    const user = normalizeUser(raw);
    if (!user || isExternalAssignee(raw) || isExternalAssignee(user) || seen.has(user.id)) continue;
    seen.add(user.id);
    out.push(user);
  }
  return out;
}

/**
 * Newest selections first, capped at ``limit``.  ``incoming`` is prepended
 * so the people just picked become the next session's recents.
 */
export function rememberRecentUsers(
  existing,
  incoming,
  limit = RECENT_ASSIGNEE_LIMIT,
) {
  const merged = [];
  const byId = new Map();
  for (const raw of [...(incoming || []), ...(existing || [])]) {
    const user = normalizeUser(raw);
    if (!user || isExternalAssignee(raw) || isExternalAssignee(user)) continue;
    const next = mergeUser(byId.get(user.id), user);
    byId.set(user.id, next);
    if (!merged.some((row) => row.id === user.id)) merged.push(next);
  }
  return merged.slice(0, limit).map((row) => byId.get(row.id));
}

function asIdSet(ids) {
  const set = new Set();
  for (const id of ids || []) {
    const n = Number(id);
    if (Number.isFinite(n)) set.add(n);
  }
  return set;
}

/**
 * Empty-query suggestions: recents first, then directory fallback, excluding
 * people already selected.  Always at most ``limit`` (default 3).
 */
export function defaultSuggestions({
  recents = [],
  fallback = [],
  excludeIds = [],
  limit = RECENT_ASSIGNEE_LIMIT,
} = {}) {
  const exclude = asIdSet(excludeIds);
  const out = [];
  const seen = new Set();
  for (const raw of [...recents, ...fallback]) {
    const user = normalizeUser(raw);
    if (!user || isExternalAssignee(raw) || isExternalAssignee(user)) continue;
    if (seen.has(user.id) || exclude.has(user.id)) continue;
    seen.add(user.id);
    out.push(user);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Typed-query suggestions: recents that match, then API hits, excluding
 * already-selected people.
 */
export function searchSuggestions({
  query,
  recents = [],
  results = [],
  excludeIds = [],
  limit = SEARCH_RESULT_LIMIT,
} = {}) {
  const exclude = asIdSet(excludeIds);
  const out = [];
  const seen = new Set();
  for (const raw of [...recents, ...results]) {
    const user = normalizeUser(raw);
    if (!user || isExternalAssignee(raw) || isExternalAssignee(user)) continue;
    if (seen.has(user.id) || exclude.has(user.id)) continue;
    if (!userMatchesSearch(user, query)) continue;
    seen.add(user.id);
    out.push(user);
    if (out.length >= limit) break;
  }
  return out;
}
